// framework/src/client/components/SecretsManager.js
//
// UI component for managing runtime secrets (subscription/project/agent scoped).
// Backed by SecretsManagerRpc — values are NEVER shown in the UI.
// Replaces the old file-based SecretsManager component.

import { ComponentBase } from '../core/ComponentBase.js';

export class SecretsManager extends ComponentBase {

    constructor(config) {
        super(config);
        this.dataCrud = null;
        // Optional: scope the view to a specific context
        // config.scope      — pre-filter to 'subscription'|'project'|'agent'
        // config.projectId  — pre-filter to a specific project
        // config.agentName  — pre-filter to a specific agent
        this.defaultScope    = config.scope    || null;
        this.defaultProject  = config.projectId || null;
        this.defaultAgent    = config.agentName || null;
    }

    async initialize() {
        // Build the data context passed to all RPC calls
        const dataContext = {};
        if (this.defaultScope)   dataContext.scope     = this.defaultScope;
        if (this.defaultProject) dataContext.projectId = this.defaultProject;
        if (this.defaultAgent)   dataContext.agentName = this.defaultAgent;

        this.dataCrud = await this.createManagedComponent('DataCrud', null, {
            instanceId:  `${this.config.instanceId || 'secretsManager'}-crud`,
            rpcClass:    'SecretsManagerRpc',
            rpcMethod:   'getData',
            schemaPath:  'collections/secrets',
            title:       this._getTitle(),
            idField:     '_id',

            viewConfig: {
                mode:   'grid',
                fields: [
                    {
                        name:   'scope',
                        label:  'Scope',
                        render: (value) => {
                            const colors = { subscription: 'primary', project: 'info', agent: 'secondary' };
                            return `<span class="badge bg-${colors[value] || 'secondary'}">${value}</span>`;
                        },
                    },
                    { name: 'key',   label: 'Key',   render: (v) => `<code>${v}</code>` },
                    { name: 'label', label: 'Label'  },
                    { name: 'description', label: 'Description' },
                    {
                        name:   'updatedAt',
                        label:  'Updated',
                        render: (v) => v ? new Date(v).toLocaleDateString() : '—',
                    },
                ],
                actions: [],
                filter: {
                    enabled: !this.defaultScope, // Only show scope filter when not pre-filtered
                    fields: {
                        scope: {
                            type:    'select',
                            label:   'Scope',
                            options: [
                                { value: '',             label: 'All scopes' },
                                { value: 'subscription', label: 'Subscription' },
                                { value: 'project',      label: 'Project' },
                                { value: 'agent',        label: 'Agent' },
                            ],
                        },
                    },
                },
                toolbar: {
                    enabled: true,
                    create:  { enabled: true, label: 'Add Secret' },
                },
            },

            formConfig: {
                mode:   'edit',
                layout: 'standard',
                fields: {
                    scope: {
                        type:         'select',
                        label:        'Scope',
                        options: [
                            { value: 'subscription', label: 'Subscription — shared across all agents' },
                            { value: 'project',      label: 'Project — scoped to a specific project'  },
                            { value: 'agent',        label: 'Agent — private to one agent'             },
                        ],
                        readonlyWhen: (d) => !!d?._id,
                        defaultValue: this.defaultScope || 'subscription',
                    },
                    projectId: {
                        type:        'text',
                        label:       'Project ID',
                        placeholder: 'Required for scope: project',
                        visibleWhen: (d) => d?.scope === 'project',
                        dependsOn:   ['scope'],
                    },
                    agentName: {
                        type:        'text',
                        label:       'Agent Name',
                        placeholder: 'Required for scope: agent',
                        visibleWhen: (d) => d?.scope === 'agent',
                        dependsOn:   ['scope'],
                    },
                    key: {
                        type:         'text',
                        label:        'Key',
                        placeholder:  'e.g. openweather_api_key',
                        readonlyWhen: (d) => !!d?._id,
                    },
                    label: {
                        type:        'text',
                        label:       'Label',
                        placeholder: 'e.g. OpenWeather API Key',
                    },
                    value: {
                        type:        'textarea',
                        label:       'Value',
                        placeholder: 'Enter secret value. Encrypted server-side immediately. Leave blank to keep existing when editing.',
                        rows:        3,
                    },
                    description: {
                        type:  'text',
                        label: 'Description',
                    },
                },
                sections: [
                    { title: 'Scope & Key',  fields: ['scope', 'projectId', 'agentName', 'key', 'label'] },
                    { title: 'Secret Value', fields: ['value', 'description'] },
                ],
                actions: { save: true, cancel: true },
            },

            dataConfig: {
                autoLoad: false,
                pageSize: 50,
                context:  dataContext,
            },
        });

        // super.initialize() must be called AFTER dataCrud is created
        // because it triggers initAfterLogin() immediately if user is already logged in.
        await super.initialize();
    }

    async initAfterLogin() {
        await this.dataCrud.refreshData();
    }

    async render() {
        if (!this.dataCrud) await this.initialize();
        return await this.dataCrud.render();
    }

    _getTitle() {
        if (this.defaultAgent)   return `Secrets — Agent: ${this.defaultAgent}`;
        if (this.defaultProject) return `Secrets — Project`;
        if (this.defaultScope)   return `${this.defaultScope.charAt(0).toUpperCase() + this.defaultScope.slice(1)} Secrets`;
        return 'Secrets Management';
    }

    destroy() {
        this.dataCrud?.destroy();
        this.dataCrud = null;
        super.destroy();
    }
}